import { NavLink, useNavigate } from "react-router-dom";
import { LayoutGrid, Users, Plug, Sparkles, Settings as SettingsIcon, LogOut, Plus, ChevronRight } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useState } from "react";
import AddClientDialog from "@/components/AddClientDialog";

const nav = [
    { to: "/dashboard", label: "Dashboard", icon: LayoutGrid, testid: "nav-dashboard" },
    { to: "/clients", label: "Clients", icon: Users, testid: "nav-clients" },
    { to: "/integrations", label: "Integrations", icon: Plug, testid: "nav-integrations" },
    { to: "/ai-updates", label: "AI Updates", icon: Sparkles, testid: "nav-ai-updates" },
    { to: "/settings", label: "Settings", icon: SettingsIcon, testid: "nav-settings" },
];

export default function AppShell({ children, breadcrumbs = [], onClientCreated }) {
    const { user, workspace, logout } = useAuth();
    const navigate = useNavigate();
    const [addOpen, setAddOpen] = useState(false);

    const initials = (user?.name || user?.email || "U")
        .split(" ")
        .map((s) => s[0])
        .join("")
        .slice(0, 2)
        .toUpperCase();

    return (
        <div className="min-h-screen bg-[#FAFAFA] flex">
            {/* Sidebar */}
            <aside className="w-64 shrink-0 hidden lg:flex flex-col border-r border-slate-200 bg-white">
                <div className="px-6 py-6 border-b border-slate-200">
                    <Logo />
                </div>
                <nav className="flex-1 px-3 py-6 space-y-1">
                    {nav.map((n) => (
                        <NavLink
                            key={n.to}
                            to={n.to}
                            data-testid={n.testid}
                            className={({ isActive }) =>
                                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                                    isActive
                                        ? "bg-[#FEF2F2] text-[#C55A46]"
                                        : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                                }`
                            }
                        >
                            <n.icon className="w-4 h-4" />
                            {n.label}
                        </NavLink>
                    ))}
                </nav>
                <div className="p-3 border-t border-slate-200">
                    <button
                        data-testid="sidebar-add-client-btn"
                        onClick={() => setAddOpen(true)}
                        className="btn-terra w-full flex items-center justify-center gap-2 rounded-lg text-sm font-medium py-2.5"
                    >
                        <Plus className="w-4 h-4" /> Add Client
                    </button>
                </div>
                <div className="p-3 border-t border-slate-200">
                    <div className="flex items-center gap-3 px-2 py-2">
                        <Avatar className="w-9 h-9">
                            <AvatarImage src={user?.picture} />
                            <AvatarFallback className="bg-slate-100 text-slate-600 text-xs font-medium">{initials}</AvatarFallback>
                        </Avatar>
                        <div className="min-w-0 flex-1">
                            <div className="text-sm font-medium text-slate-900 truncate" data-testid="user-name">
                                {user?.name || user?.email}
                            </div>
                            <div className="text-xs text-slate-500 truncate">{workspace?.company_name}</div>
                        </div>
                        <button
                            data-testid="logout-btn"
                            onClick={async () => { await logout(); navigate("/login"); }}
                            className="p-2 rounded-md text-slate-400 hover:text-slate-700 hover:bg-slate-50 transition-colors"
                            title="Sign out"
                        >
                            <LogOut className="w-4 h-4" />
                        </button>
                    </div>
                </div>
            </aside>

            {/* Main */}
            <div className="flex-1 min-w-0 flex flex-col">
                <header className="glass-nav sticky top-0 z-30">
                    <div className="px-6 lg:px-10 py-4 flex items-center gap-4">
                        <div className="lg:hidden">
                            <Logo size="sm" />
                        </div>
                        <div className="hidden lg:flex items-center gap-2 text-sm">
                            {breadcrumbs.length === 0 && (
                                <span className="text-slate-500">Home</span>
                            )}
                            {breadcrumbs.map((b, i) => (
                                <span key={i} className="flex items-center gap-2">
                                    <span className={i === breadcrumbs.length - 1 ? "text-slate-900 font-medium" : "text-slate-500"}>{b}</span>
                                    {i < breadcrumbs.length - 1 && <ChevronRight className="w-3.5 h-3.5 text-slate-300" />}
                                </span>
                            ))}
                        </div>
                        <div className="ml-auto flex items-center gap-3">
                            <button
                                data-testid="header-add-client-btn"
                                onClick={() => setAddOpen(true)}
                                className="btn-terra hidden md:inline-flex items-center gap-2 rounded-lg text-sm font-medium px-4 py-2"
                            >
                                <Plus className="w-4 h-4" /> Add Client
                            </button>
                        </div>
                    </div>
                </header>
                <main className="flex-1 px-6 lg:px-10 py-8">{children}</main>
            </div>

            <AddClientDialog
                open={addOpen}
                onOpenChange={setAddOpen}
                onCreated={(c) => {
                    setAddOpen(false);
                    onClientCreated?.(c);
                    navigate(`/clients/${c.client_id}`);
                }}
            />
        </div>
    );
}
